import React, { useState, useContext, useEffect } from 'react';
import { Giftee } from '../types/Giftee';
import { GifteeProperties } from 'shared/build/types';
import { Gender } from 'shared/build/enums';

type GifteesContextT = {
  giftees: Giftee[];
  gifteesIncludingNonSelected: Giftee[];
  refresh: () => void;
  selectedGender?: Gender;
  shouldGuessGender: boolean;
};

const GifteesContext = React.createContext<GifteesContextT | undefined>(
  undefined
);

export const GifteesProvider = ({
  children,
  gifteesBasicProperties,
  refresh,
  selectedGender,
  shouldGuessGender,
}: {
  children: React.ReactNode;
  gifteesBasicProperties: GifteeProperties[];
  refresh: () => void;
  selectedGender?: Gender;
  shouldGuessGender: boolean;
}) => {
  const [giftees, setGiftees] = useState<Giftee[]>([]);
  const [
    gifteesIncludingNonSelected,
    setGifteesIncludingNonSelected,
  ] = useState<Giftee[]>([]);

  useEffect(() => {
    const gifteesIncludingNonSelected: Giftee[] = gifteesBasicProperties.map(
      (basicGiftee) => {
        return Giftee.forge(basicGiftee);
      }
    );

    const giftees = gifteesIncludingNonSelected.filter((giftee) => {
      if (!shouldGuessGender || selectedGender === undefined) return true;

      return giftee.gender === selectedGender;
    });

    setGiftees(giftees);
    setGifteesIncludingNonSelected(gifteesIncludingNonSelected);
  }, [gifteesBasicProperties, selectedGender, shouldGuessGender]);

  return (
    <GifteesContext.Provider
      value={{
        giftees,
        shouldGuessGender,
        selectedGender,
        refresh,
        gifteesIncludingNonSelected,
      }}
    >
      {children}
    </GifteesContext.Provider>
  );
};

export const useGiftees = (): GifteesContextT => {
  const value = useContext(GifteesContext);

  if (value === undefined)
    throw new Error('The Giftee context had not been initialized.');

  return value;
};
